import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { DATE_RANGE_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import '@/modules/common/styles/common.css';
import { CURRENT_ENTITY_QUERY_KEY } from '@/modules/entity/queries/entity.tsx';
import { EntityItemFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { InvestorDashboardBalanceChart } from '@/modules/investor-dashboard/components/InvestorDashboardBalanceChart.tsx';
import { InvestorDashboardBalanceStatistics } from '@/modules/investor-dashboard/components/InvestorDashboardBalanceStatistics.tsx';
import { InvestorDashboardWrapperOutletContext } from '@/modules/investor-dashboard/components/InvestorDashboardWrapper.tsx';
import { InvestorInvestmentItem } from '@/modules/investor-dashboard/components/items/InvestorInvestmentItem.tsx';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { useChartBalanceQuery } from '@/modules/investor-dashboard/queries/balances.tsx';
import { useBalanceStatisticsQuery } from '@/modules/investor-dashboard/queries/investments.tsx';
import { useCurrentUserInvestmentsQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { mapDateRangeToObj } from '@/modules/issuer-dashboard/helpers/mapDateRangeToObj.ts';
import { Alert, PageLayout, Spinner } from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import {
  ActivitiesType,
  BalancesListV1SrBalancesGetParams,
  GetEntityShareholdersResponse,
} from 'api';

const InvestorDashboard = () => {
  const [params, setParams] = useSearchParams();
  const { date_range, register_id } = useOutletContext<InvestorDashboardWrapperOutletContext>();
  const entity = queryClient.getQueryData<EntityItemFullResponse>([CURRENT_ENTITY_QUERY_KEY]);
  const entityShareholders = queryClient.getQueryData<GetEntityShareholdersResponse>([
    CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY,
    entity?.entity?.id,
  ]);
  const { isLoading: isChartBalanceLoading, data: chartBalance } = useChartBalanceQuery(
    mapDateRangeToObj(date_range || '') as BalancesListV1SrBalancesGetParams,
    Number(register_id),
  );
  const {
    hasNextPage,
    fetchNextPage: fetchNextCurrentUserInvestments,
    data: currentUserInvestments,
    isLoading: isCurrentUserInvestmentsLoading,
    isFetching: isFetchingCurrentUserInvestments,
  } = useCurrentUserInvestmentsQuery({
    page: 1,
    page_size: 15,
    register_id: Number(register_id),
  });

  const { data: dataAppliedClassTransactions } = useMyActivitiesListQuery(
    {
      page: 1,
      page_size: 10,
      type: ActivitiesType.Subscription,
    },
    Number(register_id),
  );

  const isHaveSubscriptionWithPendingStatus = useMemo(() => {
    if (!dataAppliedClassTransactions?.pages?.[0]?.data?.length) return false;

    return dataAppliedClassTransactions?.pages?.[0]?.data?.some(item => item.status === 'pending');
  }, [dataAppliedClassTransactions?.pages?.[0]?.data]);

  const isHaveSubscriptionWithPaymentStatus = useMemo(() => {
    if (!dataAppliedClassTransactions?.pages?.[0]?.data?.length) return false;

    return dataAppliedClassTransactions?.pages?.[0]?.data?.some(
      item => item.status === 'awaiting_payment',
    );
  }, [dataAppliedClassTransactions?.pages?.[0]?.data]);

  const { data: balanceStatistic, isLoading: isBalanceStatisticsLoading } =
    useBalanceStatisticsQuery(register_id);

  const handleChangeDateRange = (date_range: string) => {
    params.set(DATE_RANGE_SEARCH_PARAM_KEY, date_range);
    setParams(params, { replace: false });
  };

  const totalInvestment = useMemo(() => {
    return (
      currentUserInvestments?.pages?.reduce(
        (prev, curr) =>
          (prev += curr.data.reduce((p, c) => (p += c.register_classes_info?.length ?? 0), 0)),
        0,
      ) ?? 0
    );
  }, [currentUserInvestments]);

  if (!entity || !entityShareholders) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout title="Dashboard">
      <div className="flex flex-col gap-6">
        {isHaveSubscriptionWithPendingStatus ? (
          <Alert icon={AlertBlueIcon} variant="neutral-2" className="mb-0">
            <span className="w-full text-left text-blue-700 whitespace-normal">
              You have applied for shares and your subscription is in pending status and is awaiting
              issuer approval. After issuer approves your transaction, you will receive an email
              with payment instructions.
            </span>
          </Alert>
        ) : (
          isHaveSubscriptionWithPaymentStatus && (
            <Alert icon={AlertBlueIcon} variant="neutral-2" className="mb-0">
              <span className="w-full text-left text-blue-700 whitespace-normal">
                Issuer has approved your subscription. Please check your inbox for payment
                instructions.
              </span>
            </Alert>
          )
        )}

        <InvestorDashboardBalanceStatistics
          data={balanceStatistic}
          isLoading={isBalanceStatisticsLoading}
          numberOfInvestments={totalInvestment}
        />
        <InvestorDashboardBalanceChart
          handleChangeDateRange={handleChangeDateRange}
          date_range={date_range}
          balances={chartBalance?.balances}
          current_balance={balanceStatistic?.portfolio_value}
          isLoading={isChartBalanceLoading}
        />
        {entity && entityShareholders && (
          <div className="w-full flex flex-col p-3 sm:p-6 border border-gray-200 rounded-xl">
            <p className="text-[18px] leading-[24px] mb-6">My investments</p>
            {isCurrentUserInvestmentsLoading ||
            (isFetchingCurrentUserInvestments && totalInvestment === 0) ? (
              <div className="w-full h-40 flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            ) : (
              totalInvestment === 0 && (
                <div className="flex justify-center items-center h-40 w-full text-md text-gray-600">
                  No investments yet
                </div>
              )
            )}
            <InfiniteScroll
              next={fetchNextCurrentUserInvestments}
              dataLength={currentUserInvestments?.pages?.length ?? 0}
              hasMore={hasNextPage || false}
              style={{ overflow: 'unset' }}
              loader={
                <div className="flex justify-center items-center h-16 w-full">
                  <Spinner size="md" variant="primary" />
                </div>
              }
              className="w-full"
              scrollableTarget="scrollable"
            >
              <div className="w-full flex flex-col items-start space-y-2">
                {currentUserInvestments?.pages?.map((item, i, allPage) =>
                  item?.data?.map((register, j, allDataPage) => (
                    <InvestorInvestmentItem
                      key={'InvestorInvestmentItem_' + register.id}
                      lastItem={j === allDataPage.length - 1 && i === allPage.length - 1}
                      current_entity_id={entity?.entity.id || 0}
                      shareholders={entityShareholders?.shareholders || []}
                      register_classes_info={register.register_classes_info}
                      {...register}
                    />
                  )),
                )}
              </div>
            </InfiniteScroll>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default withPrivateAccess(InvestorDashboard, Role.SR_INVESTOR);
